import * as Joi from 'joi';
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	ManyToOne,
	OneToMany,
	OneToOne,
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import ChangeScene from '../change-scenes/change-scene.entity';
import CharScene from '../char-scenes/char-scene.entity';
import CmSceneNote from '../cm-scene-notes/cm-scene-note.entity';
import { INT_EXT, UNITS } from '../enums/enums';
import { displayShootTime, displayUnit } from '../enums/enums.utils';
import Episodic from '../episodics/episodic.entity';
import HaChangeScene from '../ha-change-scenes/ha-change-scene.entity';
import HaSceneNote from '../ha-scene-notes/ha-scene-note.entity';
import { Indexable } from '../indexable';
import MuChangeScene from '../mu-change-scenes/mu-change-scene.entity';
import MuSceneNote from '../mu-scene-notes/mu-scene-note.entity';
import PictureScene from '../picture-scenes/picture-scene.entity';
import PrBudgetPropScene from '../pr-budget-prop-scenes/pr-budget-prop-scene.entity';
import PrSceneNote from '../pr-scene-notes/pr-scene-note.entity';
import ProdBase from '../prod-base.entity';
import Production from '../productions/production.entity';
import PropScene from '../prop-scenes/prop-scene.entity';
import ScriptLocation from '../script-locations/script-location.entity';
import SetScene from '../set-scenes/set-scene.entity';
import { Taggable } from '../taggable';
import { notNullOrEmptyOrUndefined } from '../utils';
import { SceneESDoc } from './scene.interfaces';

@Entity('scenes')
export default class Scene
	extends ProdBase
	implements Indexable<SceneESDoc>, Taggable
{
	static tableName = 'scenes';

	static columns = [
		'created_by',
		'date_created',
		'description',
		'episode_number',
		'id',
		'last_updated_time',
		'last_updated_user',
		'mobile_id',
		'name',
		'shoot_day',
		'omit',
		'page_length',
		'prod_id',
		'script_day',
		'script_month',
		'script_year',
		'script_page',
		'shoot_time',
		'scene_location_id',
		'description',
		'omit',
		'unit',
		'last_updated_time',
		'last_updated_user',
		'date_created',
		'created_by',
	];

	static skipUpdateColumns = [
		'id',
		'mobile_id',
		'prod_id',
		'date_created',
		'created_by',
		'last_updated_user',
		'last_updated_time',
	];

	@Column('varchar')
	mobile_id: string;

	@Column('varchar')
	name: string;

	@Column({ type: 'datetime', transformer: momentTransformer })
	shoot_day: Moment;

	@Column('varchar')
	script_day: string;

	@Column('varchar')
	script_month: string;

	@Column('varchar')
	script_year: string;

	@Column('int')
	page_length: number;

	@Column('varchar')
	script_page: string;

	@Column('int')
	shoot_time: number;

	@Column('int')
	scene_location_id: number;

	@Column('varchar')
	description: string;

	@Column('varchar')
	episode_number: string;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	omit: boolean;

	@Column('int')
	unit: UNITS;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@OneToMany((type) => ChangeScene, (changeScene) => changeScene.scene)
	changeScenes: ChangeScene[];

	@OneToMany((type) => CharScene, (charScene) => charScene.scene)
	charScenes: CharScene[];

	@OneToOne((type) => CmSceneNote, (cmSceneNote) => cmSceneNote.scene)
	cmSceneNote: CmSceneNote;

	@OneToOne((type) => HaSceneNote, (haSceneNote) => haSceneNote.scene)
	haSceneNote: HaSceneNote;

	@OneToOne((type) => MuSceneNote, (muSceneNote) => muSceneNote.scene)
	muSceneNote: MuSceneNote;

	@OneToOne((type) => PrSceneNote, (prSceneNote) => prSceneNote.scene)
	prSceneNote: PrSceneNote;

	@ManyToOne((type) => Production, (production) => production.scenes)
	@JoinColumn({
		name: 'prod_id',
	})
	production: Production;

	@ManyToOne((type) => ScriptLocation, (location) => location.scenes)
	@JoinColumn({
		name: 'scene_location_id',
	})
	location: ScriptLocation;

	@OneToMany(
		(type) => PrBudgetPropScene,
		(prBudgetPropScene) => prBudgetPropScene.scene
	)
	prBudgetPropScenes: PrBudgetPropScene[];

	@OneToMany((type) => PropScene, (propScene) => propScene.scene)
	propScenes: PropScene[];

	@OneToMany((type) => SetScene, (setScene) => setScene.scene)
	setScenes: SetScene[];

	@OneToMany((type) => HaChangeScene, (haChangeScene) => haChangeScene.scene)
	haChangeScenes: HaChangeScene[];

	@OneToMany((type) => MuChangeScene, (muChangeScene) => muChangeScene.scene)
	muChangeScenes: MuChangeScene[];

	@OneToMany((type) => PictureScene, (pictureScene) => pictureScene.scene)
	pictureScenes: PictureScene[];

	static rules() {
		return {
			...super.rules(),
			cmSceneNote: Joi.object(CmSceneNote.rules()).allow(null),
			date_created: Joi.date().allow(null),
			description: Joi.string().max(1000).allow(null, ''),
			episode_number: Joi.string().max(100).allow(null, ''),
			haSceneNote: Joi.object(HaSceneNote.rules()).allow(null),
			last_updated_time: Joi.date().allow(null),
			location: Joi.object(ScriptLocation.rules()).allow(null),
			mobile_id: Joi.string().max(36).allow(null, ''),
			muSceneNote: Joi.object(MuSceneNote.rules()).allow(null),
			name: Joi.string().max(100),
			omit: Joi.boolean(),
			page_length: Joi.number().integer().allow(null),
			prSceneNote: Joi.object(PrSceneNote.rules()).allow(null),
			scene_location_id: Joi.number().integer().allow(null),
			script_day: Joi.string().max(140).allow(null, ''),
			script_month: Joi.string().max(140).allow(null, ''),
			script_page: Joi.string().max(100).allow(null, ''),
			script_year: Joi.string().max(140).allow(null, ''),
			shoot_day: Joi.date().allow(null),
			shoot_time: Joi.number().integer().allow(null),
			unit: Joi.number().integer().allow(null),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			changeScenes: this.changeScenes,
			charScenes: this.charScenes,
			cmSceneNote: this.cmSceneNote,
			date_created: this.date_created,
			description: this.description,
			episode_number: this.episode_number,
			haChangeScenes: this.haChangeScenes,
			haSceneNote: this.haSceneNote,
			last_updated_time: this.last_updated_time,
			location: this.location,
			muChangeScene: this.muChangeScenes,
			muSceneNote: this.muSceneNote,
			name: this.name,
			omit: this.omit,
			page_length: this.page_length,
			pictureScenes: this.pictureScenes,
			prBudgetPropScenes: this.prBudgetPropScenes,
			propScenes: this.propScenes,
			prSceneNote: this.prSceneNote,
			scene_location_id: this.scene_location_id,
			script_day: this.script_day,
			script_month: this.script_month,
			script_page: this.script_page,
			script_year: this.script_year,
			setScenes: this.setScenes,
			shoot_day: this.shoot_day,
			shoot_time: this.shoot_time,
			unit: this.unit,
		};
	}

	toESDoc(data?: {
		scriptLocation?: ScriptLocation;
		production?: Production;
		episodic?: Episodic;
	}) {
		let script_location_name: string,
			episode_num = this.episode_number;

		if (data) {
			const { scriptLocation, production, episodic } = data;
			if (scriptLocation) {
				script_location_name = [
					scriptLocation.getSetting(),
					scriptLocation.name,
				].join(' ');
			} else {
				script_location_name = null;
			}

			if (production && episodic) {
				const { is_feature, shoot_method } = episodic;

				if (!is_feature && !shoot_method && production) {
					episode_num =
						production.episode_num && production.episode_num !== ''
							? production.episode_num
							: 'TBD';
				}
			}
		}

		return {
			id: this.id,
			prod_id: this.prod_id,
			name: this.name,
			episode_num,
			script_location_id: this.scene_location_id,
			script_location_name,
		};
	}

	assign(props?: GenPartial<Scene>) {
		super.assign(props);
		if (props) {
			if (typeof props.shoot_day !== 'undefined') {
				this.shoot_day = momentTransformer.from(props.shoot_day);
			}
			if (typeof props.omit !== 'undefined') {
				this.omit = booleanTransformer.from(props.omit);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<Scene>) {
		super(props);
	}

	getDisplayName() {
		if (this.episode_number && this.episode_number !== '0') {
			return `${this.episode_number}.${this.name}`;
		}
		return this.name;
	}
	getSceneAppendedScriptLocationLabel(
		productions: Production[],
		episodic: Episodic,
		sceneLocations: ScriptLocation[]
	) {
		const production = productions.find((prod) => prod.id == this.prod_id);
		const sceneName = this.getDisplaySceneName(production, episodic);
		const sceneLocation = this.getSceneNameScriptLocationLabel(
			production,
			episodic,
			sceneLocations
		);
		return `${sceneName} ${sceneLocation}`;
	}
	getDisplaySceneName(production: Production, episodic: Episodic) {
		const { is_feature, shoot_method } = episodic;

		if (is_feature && !shoot_method) {
			return this.name;
		}

		let episodeNum = this.episode_number;
		if (episodeNum && episodeNum !== '0') {
			return `${episodeNum}.${this.name}`;
		}

		if (!is_feature && !shoot_method) {
			if (production) {
				episodeNum =
					production.episode_num && production.episode_num !== ''
						? production.episode_num
						: 'TBD';
				return `${episodeNum}.${this.name}`;
			}
			return this.name;
		}

		if (!is_feature && production) {
			episodeNum =
				production.episode_num && production.episode_num !== ''
					? production.episode_num
					: 'TBD';
			return `${episodeNum}.${this.name}`;
		}
		return this.name;
	}

	getUnit() {
		return displayUnit(this.unit);
	}

	getScriptDay(space: boolean = false) {
		let day = '';
		const time = displayShootTime(this.shoot_time);
		if (typeof time !== 'undefined') {
			day += time;
		}
		if (space && day !== '') {
			day += ' ';
		}
		if (notNullOrEmptyOrUndefined(this.script_day)) {
			day += this.script_day;
		}
		return day;
	}

	getFullScriptDate(
		space: boolean = false,
		hyphenYear: boolean = false,
		pipeMonth: boolean = false,
		combineMonthYear: boolean = false
	) {
		let day = this.getScriptDay(space);
		const DDMMYY = [];
		if (notNullOrEmptyOrUndefined(day)) {
			DDMMYY.push(day);
		}
		if (
			notNullOrEmptyOrUndefined(this.script_month) ||
			notNullOrEmptyOrUndefined(this.script_year)
		) {
			DDMMYY.push(pipeMonth ? '|' : '-');
		}

		let combinedMonthYear = '';

		if (notNullOrEmptyOrUndefined(this.script_month)) {
			combineMonthYear
				? (combinedMonthYear += this.script_month)
				: DDMMYY.push(this.script_month);
		}

		if (hyphenYear && notNullOrEmptyOrUndefined(this.script_year)) {
			combineMonthYear ? (combinedMonthYear += '-') : DDMMYY.push('-');
		}

		if (notNullOrEmptyOrUndefined(this.script_year)) {
			combineMonthYear
				? (combinedMonthYear += this.script_year)
				: DDMMYY.push(this.script_year);
		}

		if (combineMonthYear) {
			DDMMYY.push(combinedMonthYear);
		}

		return DDMMYY.join(' ');
	}

	getSelectOptionWithEpisodeNum(
		production: Production,
		episodic: Episodic,
		scriptLocations: ScriptLocation[]
	) {
		return {
			label: this.getNameLocationScriptDayDescriptionLabel(
				production,
				episodic,
				scriptLocations
			),
			value: this.id,
		};
	}

	getSceneString(production: Production, episodic: Episodic) {
		const { is_feature, shoot_method } = episodic;
		let episodeNum = this.episode_number;
		if (is_feature && !shoot_method) {
			return this.name;
		}
		if (!is_feature && !shoot_method && production) {
			episodeNum =
				production.episode_num && production.episode_num !== ''
					? production.episode_num
					: 'TBD';
		} else if (
			episodeNum === null &&
			!is_feature &&
			shoot_method &&
			production
		) {
			episodeNum =
				production.episode_num && production.episode_num !== ''
					? production.episode_num
					: 'TBD';
		}
		return `${episodeNum}.${this.name}`;
	}

	getNameLocationScriptDayDescriptionLabel(
		production: Production,
		episodic: Episodic,
		sceneLocations: ScriptLocation[]
	) {
		const location = sceneLocations.find(
			(sceneLocation) => sceneLocation.id === this.scene_location_id
		);
		return `Sc. ${this.getSceneString(production, episodic)}${
			this.getScriptDay() ? ' - ' + this.getScriptDay() : ''
		}${
			location && INT_EXT[location.int_ext]
				? ' - ' + INT_EXT[location.int_ext] + '.'
				: ''
		}${location ? ' ' + location.name : ''}`;
	}

	getSceneNameScriptLocationLabel(
		production: Production,
		episodic: Episodic,
		sceneLocations: ScriptLocation[]
	) {
		const location = sceneLocations.find(
			(sceneLocation) => sceneLocation.id === this.scene_location_id
		);
		return `${
			location && INT_EXT[location.int_ext]
				? ' - ' + INT_EXT[location.int_ext] + '.'
				: ''
		}${location ? ' ' + location.name : ''}`;
	}
	getTagName(data?: {
		production: Production;
		episodic: Episodic;
		scriptLocation?: ScriptLocation;
	}) {
		if (!data) {
			return this.getDisplayName();
		}
		return [
			this.getSceneString(data.production, data.episodic),
			data.scriptLocation && data.scriptLocation.name,
		]
			.filter((field) => !!field)
			.join(' - ');
	}

	getShootDateFormatted(dateFormat: string = 'ddd, MM/DD/YY') {
		return this.shoot_day != null && this.shoot_day.isValid()
			? this.shoot_day.format(dateFormat)
			: 'TBD';
	}

	getdecodeShootTime(space: boolean = false) {
		let day = '';
		const time = displayShootTime(this.shoot_time);
		if (typeof time !== 'undefined') {
			day += time;
		}
		if (space && day !== '') {
			day += ' ';
		}
		return day;
	}
	/**crated for set breakdown ticket */
	getPageLength() {
		if (this.page_length == null) {
			return '';
		} else {
			let total_eighths = this.page_length;
			return this.pageLengthString(total_eighths);
		}
	}

	pageLengthString(eighths) {
		let whole, numerator: any;
		if (eighths > 7) {
			whole = Math.floor(eighths / 8);
			numerator = eighths % 8;
		} else {
			numerator = eighths;
			whole = 0;
		}
		let wh = whole ? whole : '';
		let flo = numerator ? numerator + '/8' : '';
		if (wh && flo) {
			return wh + ' ' + flo;
		} else {
			return wh + flo ? wh + flo : 0;
		}
	}
}
